import type { SignalTone } from '../theme/tokens';

/**
 * What a store ERP sync actually wrote. Daily scheduled Shopify/Lightspeed
 * runs are `pricing`; Compare & update and Force full are manual catalog
 * writes. Missing fields on older docs fall through to catalog.
 */
export type SyncRunKind = 'pricing' | 'compare' | 'full' | 'catalog';

export function resolveSyncRunKind(run: {
  syncType?: unknown;
  forceFullSync?: unknown;
}): SyncRunKind {
  if (run.syncType === 'pricing_only') return 'pricing';
  if (run.syncType === 'compare_update') return 'compare';
  if (run.forceFullSync === true) return 'full';
  return 'catalog';
}

export function syncRunKindLabel(kind: SyncRunKind): string {
  switch (kind) {
    case 'pricing':
      return 'Pricing only';
    case 'compare':
      return 'Compare & update';
    case 'full':
      return 'Full catalog';
    default:
      return 'Catalog';
  }
}

/** Compact label for the run table chip. */
export function syncRunKindChipLabel(kind: SyncRunKind): string {
  switch (kind) {
    case 'pricing':
      return 'Pricing';
    case 'compare':
      return 'Compare';
    case 'full':
      return 'Full';
    default:
      return 'Catalog';
  }
}

export function syncRunKindTone(kind: SyncRunKind): SignalTone {
  switch (kind) {
    case 'pricing':
      return 'info';
    case 'compare':
      return 'caution';
    case 'full':
      return 'caution';
    default:
      return 'neutral';
  }
}

/** What the Updated column counted for this kind of run. */
export function syncRunUpdatedNoun(kind: SyncRunKind): string {
  switch (kind) {
    case 'pricing':
      return 'prices';
    case 'compare':
      return 'changed';
    case 'full':
      return 'rewrites';
    default:
      return 'products';
  }
}

export function syncRunKindHint(kind: SyncRunKind): string {
  switch (kind) {
    case 'pricing':
      return 'This run only pushed advertised price and compare-at. Titles, descriptions, and images were not rewritten.';
    case 'compare':
      return 'Motzu compared each selected product to the storefront and pushed only products whose fields differed.';
    case 'full':
      return 'Force full re-sync. Motzu rewrote the selected catalog fields on every product, even if the storefront already matched.';
    default:
      return 'Catalog sync. Unchanged products are skipped when possible. Updated is products that received a catalog write — whichever fields this integration has turned on.';
  }
}

export function shopifySyncRunExtras(run: Record<string, unknown>): string[] {
  const extras: string[] = [];
  if (run.syncImagesOverride === false) extras.push('no product images in Shopify payload');
  else if (run.syncImagesOverride === true) extras.push('images explicitly on for this run');
  if (run.executionMode === 'direct') extras.push('executed directly (not queued)');
  return extras;
}

/** Fields stored on `integrationSyncs` for Shopify (queued worker + direct API). */
export function formatShopifySyncRunSummary(run: Record<string, unknown>): string {
  return [syncRunKindLabel(resolveSyncRunKind(run)), ...shopifySyncRunExtras(run)].join(' · ');
}
